import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Input, notification, Radio, Row, Select, Space, Typography } from 'antd'
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { NavLink } from 'react-router-dom'
import { RadioChangeEvent } from 'antd/lib/radio'
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'

import CardBlock from '../../CardBlock/CardBlock'
import ExpertCard from '../../ExpertCard/ExpertCard'

import { CallData, SubscriptionKind } from '../../../../context/types'
import { checkCoupon } from '../../../../context/subscription'
import { useAuthContext } from '../../../../context/auth'
import { amountToCurrencyFormat } from '../../../../utils/format'
import Countries from '../../../../utils/countries'
import { DEFAULT_COUNTRY } from '../../../../constants'
import useDevAffiliateScript from '../../../../hooks/useDevAffiliateScript'
import { ICustomSubscription } from '../UseSubscriptions'
import { currentBrand } from '../../../../config'
import { HandleBookParam } from '../BookingModal'
import { useChannelContext } from '../../../../context/channel'

const { Text, Title } = Typography
const { Option } = Select

interface StepProps {
	onBack?: () => void
	onAction?: (param: HandleBookParam) => Promise<any>
	cards: any[]
	subscriptions: ICustomSubscription[]
	call: CallData
	total?: number
}

const Step3: FunctionComponent<StepProps> = (props) => {
	const { subscriptions, cards, call, onBack, onAction, total } = props
	const idevCustomRef = useRef<HTMLInputElement>(null)

	const { auth } = useAuthContext()
	const { channel } = useChannelContext()

	const stripe = useStripe()
	const elements = useElements()

	const [loading, setLoading] = useState(false)
	const [loadingCoupon, setLoadingCoupon] = useState(false)

	const [currentSubscription, setCurrentSubscription] = useState<ICustomSubscription>(null)
	const [showCardBlock, setShowCardBlock] = useState(false)
	const [showCardField, setShowCardField] = useState(true)

	const [selectedCard, setSelectedCard] = useState<any>()
	const [bookPrice, setBookPrice] = useState(0)

	const [discount, setDiscount] = useState(0)
	const [coupon, setCoupon] = useState('')
	const [couponError, setCouponError] = useState('')

	const [phoneCountryId, setPhoneCountryId] = useState<string>('1')
	const [phoneCountryCode, setPhoneCountryCode] = useState<string>(DEFAULT_COUNTRY.iso)
	const [phone, setPhone] = useState<number>(null)
	const [phoneError, setPhoneError] = useState<string>(null)

	const isPhoneRequired = useMemo(() => {
		if (!currentSubscription) {
			return false
		}
		const currentChannel = (channel?.channels || []).find(({ id }) => currentSubscription?.channel_id === id)

		return currentChannel?.integration_config?.phone_rules?.phone_required_to_book_call ?? true
	}, [currentSubscription, channel?.channels])

	useEffect(() => {
		if (auth?.user?.phone_country_id && auth?.user?.phone) {
			setPhoneCountryId(auth?.user?.phone_country_id)
			setPhoneCountryCode(
				auth?.user?.phone_country_code ||
					parsePhoneNumber(`+${auth.user?.phone_country_id}${auth.user?.phone}`)?.country
			)
			setPhone(auth?.user?.phone)
			setPhoneError(null)
		} else if (isPhoneRequired) {
			setPhoneError('Mobile number is required')
		}
	}, [auth?.user?.phone_country_id, auth?.user?.phone, isPhoneRequired])

	useEffect(() => {
		if (cards[0]) setSelectedCard(cards[0])
	}, [cards])

	useEffect(() => {
		const left = currentSubscription?.unitsLeftThisMonth || 0
		const price = currentSubscription?.credit_based
			? [SubscriptionKind.single, SubscriptionKind.overage].includes(currentSubscription?.kind)
				? currentSubscription?.price_per_unit * currentSubscription?.quantity
				: currentSubscription?.price_per_unit || currentSubscription?.price_per_month
			: total
			? 0
			: currentSubscription?.price_per_unit || currentSubscription?.price_per_month

		setShowCardBlock(left < 1 && !currentSubscription?.isUnlimited && !!currentSubscription?.stripe_plan && !!price)
		setBookPrice(price)
	}, [currentSubscription, subscriptions])

	useDevAffiliateScript()

	const handleBook = async () => {
		const total = bookPrice - discount

		setLoading(true)
		const subscriptionTypeId = currentSubscription.primarySubscriptionTypeId || currentSubscription.type_id
		const subscriptionId =
			currentSubscription.primarySubscriptionId || currentSubscription.userSubscription?.[0]?.subscription_id
		const overageTypeId = currentSubscription.primarySubscriptionTypeId ? currentSubscription.type_id : undefined

		try {
			const cardNumberElement = elements.getElement(CardNumberElement)
			const cardCvcElement = elements.getElement(CardCvcElement)
			const cardExpiryElement = elements.getElement(CardExpiryElement)

			if (cardNumberElement && cardCvcElement && cardExpiryElement && !!total) {
				const { error, paymentMethod } = await stripe.createPaymentMethod({
					type: 'card',
					card: cardNumberElement,
				})

				if (!selectedCard && !paymentMethod && total !== 0) {
					notification.error({ message: 'Error', description: 'No payment method connected' })
					return
				}

				if (!!error) throw Error(error.message)

				await onAction({
					subscriptionTypeId,
					subscriptionId,
					overageTypeId,
					paymentMethodId: paymentMethod.id,
					coupon,
					phoneCountryId,
					phoneCountryCode,
					phone,
					quantity: currentSubscription.quantity,
					metadata: {
						ip: idevCustomRef.current.value,
					},
				})
			} else {
				// tslint:disable-next-line: prettier
				await onAction({
					subscriptionTypeId,
					subscriptionId,
					overageTypeId,
					paymentMethodId: selectedCard?.id || '',
					coupon,
					phoneCountryId,
					phoneCountryCode,
					phone,
					quantity: currentSubscription.quantity,
					metadata: {
						ip: idevCustomRef.current.value,
					},
				})
			}
		} catch (error) {
			notification.error({ message: 'Error', description: error.message || 'Oops! Something went wrong!' })
		} finally {
			setLoading(false)
		}
	}

	const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCoupon(e.target.value)
		setCouponError('')
	}

	const handleCheckCoupon = async () => {
		setLoadingCoupon(true)
		try {
			const productId = currentSubscription.stripe_plan

			const price = currentSubscription?.credit_based
				? [SubscriptionKind.single, SubscriptionKind.overage].includes(currentSubscription?.kind)
					? currentSubscription?.price_per_unit * currentSubscription?.quantity
					: currentSubscription?.price_per_unit || currentSubscription?.price_per_month
				: total
				? 0
				: currentSubscription?.price_per_unit || currentSubscription?.price_per_month

			const r = await checkCoupon(coupon, productId)
			const nextDiscount = r.coupon.amount_off || (price * r.coupon.percent_off) / 100 || 0

			setDiscount(nextDiscount)

			if (price === nextDiscount) setShowCardField(false)
		} catch (error) {
			setDiscount(0)
			setCouponError(error.message || 'Invalid coupon code')
		} finally {
			setLoadingCoupon(false)
		}
	}

	const handleChangeSubscription = (id: number) => {
		setCurrentSubscription(subscriptions.find((v) => v.type_id === id))
		setDiscount(0)
		setCouponError('')
		setCoupon('')
		setShowCardField(true)
	}

	const changePhoneCountryId = useCallback(
		(value: string) => {
			const newCountryCallingCode = getCountryCallingCode(value)
			setPhoneCountryId(newCountryCallingCode)
			setPhoneCountryCode(value)
			if (phone) {
				if (!isValidPhoneNumber(`+${newCountryCallingCode}${phone}`)) {
					setPhoneError('The input is not valid mobile number!')
				} else {
					setPhoneError(null)
				}
			} else {
				setPhoneError('Mobile number is required')
			}
		},
		[phone]
	)
	const changePhone = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setPhone(+e.target.value || null)
			if (e.target.value) {
				if (!isValidPhoneNumber(`+${phoneCountryId}${e.target.value}`)) {
					setPhoneError('The input is not valid mobile number!')
				} else {
					setPhoneError(null)
				}
			} else {
				setPhoneError('Mobile number is required')
			}
		},
		[phoneCountryId]
	)

	const isBookDisabled = useMemo(() => {
		return (
			!currentSubscription ||
			(currentSubscription?.userSubscription?.[0]?.subscription_id &&
				!(currentSubscription?.unitsLeftThisMonth > 0) &&
				!currentSubscription?.isUnlimited) ||
			Boolean(phoneError)
		)
	}, [subscriptions, currentSubscription, phoneError])

	const prefixSelector = (
		<Select
			filterOption={(input, option) => {
				return (
					option['data-code'].toLowerCase().indexOf(input.toLowerCase().replace('+', '')) >= 0 ||
					option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
				)
			}}
			defaultValue={DEFAULT_COUNTRY.iso}
			showSearch
			style={{ width: 105 }}
			value={phoneCountryCode}
			onChange={changePhoneCountryId}
		>
			{Countries.options.map(({ code, flag, country }, index) => (
				<Option key={index} value={`${country}`} data-code={code}>
					<span className="country-flag">{flag}</span>
					<span className="country-code">+{code}</span>
				</Option>
			))}
		</Select>
	)

	return (
		<section className="RequestCallModalStep3">
			<Row gutter={[16, 16]}>
				<Col xs={{ span: 24, order: 2 }} lg={{ span: 15, order: 1 }}>
					<Space direction="vertical" style={{ width: '99%' }}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<h3>Payment Options</h3>
							{total !== null && !channel.selectedChannel?.integration_config.hide_call_pricing && (
								<h4>
									Available {currentBrand.creditBased ? 'credits: ' : 'calls: '}
									<NavLink to="/profile/subscriptions" target="_blank">
										{total}
									</NavLink>
								</h4>
							)}
						</div>
						<SubscriptionsList
							value={currentSubscription?.type_id}
							list={subscriptions}
							onChange={handleChangeSubscription}
							total={total}
						/>
						<div>
							<Title level={5}>Mobile Phone (for reminders)</Title>
							<Input
								addonBefore={prefixSelector}
								style={{ width: '100%' }}
								type="number"
								value={phone}
								required={isPhoneRequired}
								className={Boolean(phoneError) ? 'ant-form-item-has-error' : ''}
								onChange={changePhone}
							/>
							<Text type="danger">{phoneError}</Text>
						</div>
						{showCardBlock && (
							<>
								<h3>Payment method</h3>
								{showCardField && <CardBlock card={selectedCard} modalView hideAddBtn showCancelCardBtn={false} />}
								<Space className="CouponInput">
									<Input placeholder={'Coupon Code'} size="large" value={coupon} onChange={handleCouponChange} />
									<Button
										type="default"
										shape="round"
										size="large"
										disabled={!coupon}
										loading={loadingCoupon}
										onClick={handleCheckCoupon}
										children={'Apply coupon'}
									/>
								</Space>
								{!!couponError && !discount && <div className="CouponInputError">{couponError}</div>}
								{!!discount && (
									<div className="CouponInputResult">
										Coupon code applied. ${amountToCurrencyFormat(discount)} discount added.
									</div>
								)}
							</>
						)}
					</Space>
				</Col>
				<Col xs={{ span: 24, order: 1 }} lg={{ span: 9, order: 2 }}>
					<ExpertCard call={call} timezone={auth?.user?.timezone} />
				</Col>
			</Row>

			<footer style={{ marginTop: 25 }}>
				<Space>
					<Button type="default" shape="round" size="large" onClick={onBack}>
						Back
					</Button>
					<Button
						type="primary"
						shape="round"
						size="large"
						onClick={handleBook}
						loading={loading}
						disabled={isBookDisabled}
					>
						{showCardBlock && bookPrice
							? `${
									!currentSubscription?.userSubscription && currentSubscription.kind === SubscriptionKind.monthly
										? 'Subscribe and '
										: ''
							  } Book for $${amountToCurrencyFormat(bookPrice - discount)}`
							: 'Book'}
					</Button>
					{/* Hidden field for IDevAffiliate service, please don't change id */}
					<input type="hidden" ref={idevCustomRef} id="idev_custom_x21" />
				</Space>
			</footer>
		</section>
	)
}

export default Step3

// --------------------------------------------------------------------------------
// tslint:disable

interface SubscriptionsListProps {
	value: number
	list: ICustomSubscription[]
	total?: number
	onChange: (value: number) => void
}

const SubscriptionsList: FunctionComponent<SubscriptionsListProps> = (props) => {
	const { value, list, total, onChange } = props
	const handleChange = (e: RadioChangeEvent) => onChange(e.target.value)
	const parseDesc = (text: string) => {
		return text?.replaceAll('+', '\n')
	}

	const { singleCallSubscriptions, monthlySubscriptions, isSubscriptionOwner } = useMemo(() => {
		const monthlySubscriptions = list
			.filter((v) => v.kind === SubscriptionKind.monthly)
			.sort((a, b) => {
				return (b.unitsLeftThisMonth || 0) - (a.unitsLeftThisMonth || 0)
			})
		return {
			singleCallSubscriptions: list.filter((v) => [SubscriptionKind.single, SubscriptionKind.overage].includes(v.kind)),
			monthlySubscriptions,
			isSubscriptionOwner: monthlySubscriptions.some((v) => v.userSubscription?.length),
		}
	}, [list])

	return (
		<Radio.Group onChange={handleChange} value={value} className="RadioGroup">
			{singleCallSubscriptions?.length ? (
				<div className="single-call">
					<Title level={5} style={{ marginBottom: 10 }}>
						Single {currentBrand.creditBased ? `call` : 'credit'}
					</Title>
					<div className="payment-options">
						{singleCallSubscriptions.map((singleCallSub: ICustomSubscription) => {
							const price = singleCallSub.credit_based
								? singleCallSub.price_per_unit * singleCallSub.quantity
								: total
								? 0
								: singleCallSub?.price_per_unit || singleCallSub?.price_per_month

							return (
								<Radio value={singleCallSub.type_id} key={singleCallSub.type_id} className="Radio">
									<Text style={{ fontWeight: 'normal', whiteSpace: 'pre-line' }}>
										<span>
											<span>{`$${amountToCurrencyFormat(price)} - ${
												singleCallSub.quantity ? singleCallSub.quantity + ' credits - ' : ''
											} `}</span>
											{!singleCallSub.stripe_plan && <span>{`from ${singleCallSub.channel.name} - `}</span>}
											<b>{singleCallSub.name}</b>
										</span>
										<br />
										<small>{parseDesc(singleCallSub.description)}</small>
									</Text>
								</Radio>
							)
						})}
					</div>
				</div>
			) : (
				''
			)}
			{!!monthlySubscriptions.length && (
				<>
					{singleCallSubscriptions?.length && (
						<Title level={5} style={{ margin: '10px 0' }}>
							OR
						</Title>
					)}
					<div className="payment-options">
						<Title level={5} style={{ marginBottom: 10 }}>
							{isSubscriptionOwner ? `Use your subscription` : `Join as a member and save!`}
						</Title>
						{monthlySubscriptions.map((monthlySub) => (
							<Radio
								value={monthlySub.type_id}
								key={monthlySub.type_id}
								className="Radio"
								disabled={
									monthlySub.userSubscription?.[0]?.subscription_id &&
									!(monthlySub.unitsLeftThisMonth > 0) &&
									!monthlySub.isUnlimited
								}
							>
								<Text
									style={{
										fontWeight: monthlySub.userSubscription?.[0]?.subscription_id ? 'bold' : 'normal',
										whiteSpace: 'pre-line',
									}}
								>
									{monthlySub.userSubscription?.[0]?.subscription_id ? (
										<>
											<span>{monthlySub.name}</span>
											{!monthlySub.stripe_plan && (
												<small style={{ opacity: 0.5 }}> from {monthlySub.channel.name}</small>
											)}
											<br />
											<small>{parseDesc(monthlySub.description)}</small>
											<br />
											<small style={{ fontWeight: 'normal' }}>
												{monthlySub.isUnlimited
													? ''
													: `${monthlySub.credit_based ? 'Credits' : 'Calls'} left this month: ` +
													  monthlySub.unitsLeftThisMonth}
											</small>
										</>
									) : (
										<>
											{!monthlySub.stripe_plan ? (
												<span>
													<span>{`from ${monthlySub.channel.name} - `}</span>
													<b>{monthlySub.name}</b>
												</span>
											) : (
												<span>
													<span>{`$${amountToCurrencyFormat(
														monthlySub.price_per_month || monthlySub.price_per_unit
													)}/month - `}</span>
													<b>{monthlySub.name}</b>
												</span>
											)}
											<br />
											<small>{parseDesc(monthlySub.description)}</small>
										</>
									)}
								</Text>
							</Radio>
						))}
					</div>
				</>
			)}
		</Radio.Group>
	)
}
